import { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Divider, Paper, Tab, Tabs, CircularProgress, Alert } from '@mui/material';
import axiosInstance from '../../../Config/axiosInstance';
import './HistoryPurchaseOrder.css';

/*functii helper*/
const roundToFourDecimals = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const num = parseFloat(value);
    if (isNaN(num)) return 0;
    return Math.round(num * 10000) / 10000;
};

const formatNumber = (value, locale = 'ro-RO') => {
    if (value === null || value === undefined || value === '') return '';
    const n = Number(value);
    if (Number.isNaN(n)) return '';
    return new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(n);
};

const normalizeDecimal = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const number = parseFloat(value);
    if (isNaN(number)) return '';
    return new Intl.NumberFormat('ro-RO', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 4,
    }).format(number);
};

const formatDateToRomanian = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
};

const getStatusClass = (status) => {
    const map = {
        RECEIVED: 'po-status-received',
        DRAFT: 'po-status-draft',
        ORDERED: 'po-status-ordered',
        CANCELED: 'po-status-canceled',
        'WAITING APPROVAL': 'po-status-wappr',
        APPROVED: 'po-status-approved',
        REJECTED: 'po-status-rejected',
        'PARTIALLY RECEIVED': 'po-status-partially',
        REVISED: 'po-status-revised',
    };
    return `po-status-badge ${map[status] ?? 'po-status-draft'}`;
};

/*componenta principala*/
const HistoryPORevisions = () => {
    const navigate = useNavigate();
    const { id } = useParams();

    const [revisions, setRevisions] = useState([]);
    const [tabIndex, setTabIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');

    const [invoices, setInvoices] = useState([]);
    const [loadingInvoices, setLoadingInvoices] = useState(false);

    /*FETCH — facturi pentru revizia curenta */
    const fetchInvoices = useCallback(async (poId) => {
        if (!poId) return;
        try {
            setLoadingInvoices(true);
            const cleanedId = String(poId).replace(/\/$/, '');
            const response = await axiosInstance.get(
                `/purchase-order/${cleanedId}/invoice_numbers/`
            );
            setInvoices(response.data.invoice_numbers || []);
        } catch (error) {
            console.error('Error fetching invoices: ', error);
            setInvoices([]);
        } finally {
            setLoadingInvoices(false);
        }
    }, []);

    /*FETCH — revizii */
    const fetchRevisions = useCallback(async () => {
        try {
            setLoading(true);
            setErrorMessage('');
            const response = await axiosInstance.get(
                `/purchase-order/${id}/revisions/`
            );
            const sorted = [...response.data].sort(
                (a, b) => (a.revision_number ?? 0) - (b.revision_number ?? 0)
            );
            setRevisions(sorted);
            if (sorted.length > 0) setTabIndex(sorted.length - 1);
        } catch (error) {
            console.error('Error fetching revisions:', error);
            setErrorMessage('Failed to fetch revision history.');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchRevisions();
    }, [fetchRevisions]);

    //revizia curenta
    const currentRevision = revisions[tabIndex] || null;

    //reincarca facturile cand se schimba revizia
    useEffect(() => {
        if (currentRevision?.id) {
            fetchInvoices(currentRevision.id);
        } else {
            setInvoices([]);
        }
    }, [currentRevision?.id, fetchInvoices]);

    const view = useMemo(() => {
        if (!currentRevision) return null;

        const rev = currentRevision;

        const exchangeRate = rev.exchange_rate_value
            ? parseFloat(rev.exchange_rate_value)
            : null;

        const isSysCurr = !exchangeRate || exchangeRate === 1;

        const totalWoVat = parseFloat(rev.total_wo_vat) || 0;
        const vatValue = parseFloat(rev.vat_value) || 0;
        const totalValue = parseFloat(rev.total) || 0;
        const taxValueNum = parseFloat(rev.tax_value) || 0;

        const hasSystemTotals = rev.total_system_currency != null
            && rev.total_wo_vat_system != null;

        const totalWoVatSystem = hasSystemTotals
            ? parseFloat(rev.total_wo_vat_system) || 0
            : (!isSysCurr && exchangeRate
                ? roundToFourDecimals(totalWoVat * exchangeRate)
                : totalWoVat);

        const vatValueSystem = hasSystemTotals
            ? parseFloat(rev.vat_value_system) || 0
            : (!isSysCurr && exchangeRate
                ? roundToFourDecimals(vatValue * exchangeRate)
                : vatValue);

        const totalSystemCurrency = hasSystemTotals
            ? parseFloat(rev.total_system_currency) || 0
            : (!isSysCurr && exchangeRate
                ? roundToFourDecimals(totalValue * exchangeRate)
                : totalValue);

        const taxValueSystem = rev.tax_value_system != null
            ? parseFloat(rev.tax_value_system) || 0
            : (!isSysCurr && exchangeRate
                ? roundToFourDecimals(taxValueNum * exchangeRate)
                : taxValueNum);

        // items
        const products = (rev.items || []).map((item) => {
            const itemTotal = parseFloat(item.total) || 0;
            const itemTotalSystem = item.total_system_currency != null
                ? parseFloat(item.total_system_currency) || 0
                : (!isSysCurr && exchangeRate
                    ? roundToFourDecimals(itemTotal * exchangeRate)
                    : itemTotal);

            return {
                purchase_order_item_id: item.id,
                id: item.product_detail?.id ?? item.product,
                title: item.product_detail?.title ?? item.product_name ?? 'Unknown',
                quantity: item.quantity,
                vat: item.vat_value,
                um: item.unit,
                cost: item.unit_price,
                total: item.total,
                total_system_currency: itemTotalSystem,
                arrival_deadline: item.arrival_deadline,
                vat_value_tax: item.vat_value_tax,
            };
        });

        const vatValueTax = rev.vat_value_tax
            ?? rev.items?.[0]?.vat_value_tax
            ?? '';

        return {
            // mappings din serializer
            supplierName: rev.supplier_name ?? '',
            supplierAddress: rev.supplier_address ?? '',
            deliveryName: rev.delivery_address_name ?? '',
            deliveryAddress: rev.delivery_address_full ?? '',
            projectName: rev.project_code_name ?? '',
            subprojectName: rev.subproject_code_name ?? '',
            currencyName: rev.currency_name ?? '',
            currencyAbbreviation: rev.currency_abbreviation ?? '',
            taxName: rev.tax_name ?? '',
            exchangeRate,
            exchangeRateDate: rev.exchange_rate_date,
            isSysCurr,
            // totaluri
            totalWoVat,
            vatValue,
            totalValue,
            taxValueNum,
            totalWoVatSystem,
            vatValueSystem,
            totalSystemCurrency,
            taxValueSystem,
            vatValueTax,
            // items
            products,
        };
    }, [currentRevision]);

    const systemCurrencyLabel = useMemo(() => {
        if (!currentRevision) return '';
        return currentRevision.system_currency_name
            ?? currentRevision.system_currency_abbreviation
            ?? '';
    }, [currentRevision]);

    //handlers
    const handleRevisionChange = (_, newValue) => setTabIndex(newValue);

    if (loading) {
        return (
            <div className="revisions-po-page">
                <div className="history-po-loading">
                    <CircularProgress />
                </div>
            </div>
        );
    }

    if (errorMessage) {
        return (
            <div className="revisions-po-page">
                <Alert severity="error">{errorMessage}</Alert>
            </div>
        );
    }

    if (revisions.length === 0 || !view || !currentRevision) {
        return (
            <div className="revisions-po-page">
                <Alert severity="info">
                    No revisions available for this purchase order.
                </Alert>
            </div>
        );
    }

    const rev = currentRevision;
    return (
        <div className="revisions-po-page">
            {/* HEADER */}
            <div className="history-po-header">
                <div className="history-po-header-left">
                    <ArrowBackIcon className="back-arrow" onClick={() => navigate(-1)} />

                    <div className="history-po-title-wrap">
                        <h2 className="history-po-title">REVISION HISTORY</h2>
                        <p className="history-po-subtitle">
                            <span className="history-po-subtitle-icon">📋</span>
                            <span className="history-po-subtitle-text">
                                All revisions for the selected purchase order, including the current one.
                            </span>
                        </p>
                    </div>
                </div>
            </div>

            <Divider className="history-po-header-divider" />

            {/* TABS */}
            <Paper className="history-po-tabs-card" elevation={0}>
                <div className="history-po-tabs-wrapper">
                    <Tabs
                        value={tabIndex}
                        onChange={handleRevisionChange}
                        variant="standard"
                        scrollButtons={false}
                        className="history-po-tabs"
                        TabIndicatorProps={{ className: 'history-po-tabs-indicator' }}
                    >
                        {revisions.map((revision) => {
                            const isOriginal =
                                revision.revision_number === 0 ||
                                revision.revision_number === null;
                            const revisionLabel = isOriginal
                                ? 'Original'
                                : `Rev ${revision.revision_number}`;
                            return (
                                <Tab
                                    key={revision.id}
                                    label={revisionLabel}
                                    className="history-po-tab"
                                />
                            );
                        })}
                    </Tabs>
                </div>
            </Paper>

            {/* PO NUMBER + STATUS */}
            <Paper className="history-po-po-card" elevation={0}>
                <div className="history-po-po-info">
                    <div className="history-po-po-number">
                        <span className="history-po-po-value">
                            {rev.order_number || '-'}
                        </span>
                    </div>
                    <div className="history-po-po-status">
                        <span className={getStatusClass(rev.status)}>
                            {rev.status || 'N/A'}
                        </span>
                    </div>
                </div>
            </Paper>

            {/* REVISION NOTES */}
            {rev.note_revision && (
                <div className="card card-select custom-card revision-notes-card">
                    <h4>REVISION NOTES</h4>
                    <div className="revision-notes-content">
                        <p>{rev.note_revision}</p>
                    </div>
                </div>
            )}

            {/* SUPPLIER / DELIVERY / PROJECT */}
            <div className="card card-select custom-card">
                <div className="grid-3">
                    <div>
                        <h4>SUPPLIER</h4>
                        {view.supplierName && (
                            <p className="supplier-address">
                                <strong>{view.supplierName}</strong>
                            </p>
                        )}
                        {view.supplierAddress && (
                            <p className="supplier-address">{view.supplierAddress}</p>
                        )}
                    </div>

                    <div>
                        <h4>DELIVERY ADDRESS</h4>
                        {view.deliveryName && (
                            <p className="delivery-address">
                                <strong>{view.deliveryName}</strong>
                            </p>
                        )}
                        {view.deliveryAddress && (
                            <p className="delivery-address">{view.deliveryAddress}</p>
                        )}
                        <div style={{ marginTop: '16px' }}></div>
                        <p className="delivery-address">
                            <strong>DELIVERY CONDITIONS</strong>
                        </p>
                        <p className="delivery-address">
                            {rev.delivery_conditions || 'N/A'}
                        </p>
                    </div>

                    <div>
                        <h4>PROJECT CODE</h4>
                        {view.projectName && (
                            <p className="project-code">
                                <strong>{view.projectName}</strong>
                            </p>
                        )}
                        <h4 style={{ marginTop: '35px' }}>SUB-PROJECT CODE</h4>
                        {view.subprojectName && (
                            <p className="project-code">
                                <strong>{view.subprojectName}</strong>
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* SHIPMENT / PAYMENT / INVOICE / CURRENCY */}
            <div className="card card-select custom-card">
                <div className="grid-4">
                    <div>
                        <h4>SHIPMENT DETAILS</h4>
                        <p className="details">
                            <strong>ARRIVAL DEADLINE</strong>
                        </p>
                        <p className="details">
                            {formatDateToRomanian(rev.arrival_deadline) || 'N/A'}
                        </p>
                    </div>

                    <div>
                        <h4>PAYMENT</h4>
                        <p className="details">
                            <strong>PAYMENT TERM</strong>
                        </p>
                        <p className="details">{rev.payment_term || 'N/A'} days</p>
                    </div>

                    <div>
                        <h4>INVOICE</h4>
                        {loadingInvoices ? (
                            <p className="details">Loading invoices...</p>
                        ) : invoices.length > 0 ? (
                            <div className="invoices-list">
                                {invoices.map((invoiceNumber, idx) => (
                                    <div key={idx} className="invoice-item">
                                        <p className="details">{invoiceNumber}</p>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="details">No invoices</p>
                        )}
                    </div>

                    <div>
                        <h4>CURRENCY &amp; EXCHANGE RATE</h4>
                        <p className="details">
                            <strong>CURRENCY</strong>
                        </p>
                        <p className="details">{view.currencyName || 'N/A'}</p>

                        <p className="details" style={{ marginTop: '12px' }}>
                            <strong>EXCHANGE RATE</strong>
                        </p>
                        {!view.isSysCurr ? (
                            view.exchangeRate ? (
                                <p className="details">
                                    {normalizeDecimal(view.exchangeRate)}
                                    {view.exchangeRateDate &&
                                        ` (${formatDateToRomanian(view.exchangeRateDate)})`}
                                </p>
                            ) : (
                                <p className="details" style={{ color: '#999' }}>
                                    No exchange rate available
                                </p>
                            )
                        ) : (
                            <p className="details" style={{ color: '#999', fontStyle: 'italic' }}>
                                Not needed
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* PRODUCTS TABLE */}
            <div className="card">
                <h4>PRODUCTS</h4>
                {view.products.length === 0 ? (
                    <p className="no-product">No products in this revision</p>
                ) : (
                    <div className="table-responsive">
                        <table className="po-items-table">
                            <thead>
                                <tr>
                                    <th className="text-left">Product</th>
                                    <th className="text-center">Quantity</th>
                                    <th className="text-center">UM</th>
                                    <th className="text-center">VAT (%)</th>
                                    <th className="text-center">Price per unit</th>
                                    <th className="text-center">
                                        Total ({view.currencyName})
                                    </th>
                                    <th className="text-center">
                                        System Total ({systemCurrencyLabel})
                                    </th>
                                    <th className="text-center">Arrival Deadline</th>
                                </tr>
                            </thead>
                            <tbody>
                                {view.products.map((item) => (
                                    <tr key={item.purchase_order_item_id}>
                                        <td
                                            className="text-left"
                                            style={{ color: '#0831eaff', fontWeight: 'bold' }}
                                        >
                                            {item.title}
                                        </td>
                                        <td className="text-center">
                                            {normalizeDecimal(item.quantity)}
                                        </td>
                                        <td className="text-center">{item.um}</td>
                                        <td className="text-center">
                                            {normalizeDecimal(item.vat)}
                                        </td>
                                        <td className="text-center">
                                            {normalizeDecimal(item.cost)}
                                        </td>
                                        <td className="text-center">
                                            {formatNumber(item.total)}
                                        </td>
                                        <td
                                            className="text-center"
                                            style={{ color: '#2e5bee', fontWeight: '500' }}
                                        >
                                            {formatNumber(item.total_system_currency || 0)}{' '}
                                            {systemCurrencyLabel}
                                        </td>
                                        <td className="text-center">
                                            {formatDateToRomanian(item.arrival_deadline)}
                                        </td>
                                    </tr>
                                ))}

                                {/* TAX ROW */}
                                <tr className="history-po-tax-row">
                                    <td className="text-left" colSpan={3}>
                                        <span>
                                            <b>Taxes - {view.taxName || 'N/A'}</b>
                                        </span>
                                    </td>
                                    <td className="text-center">
                                        {normalizeDecimal(view.vatValueTax)}
                                    </td>
                                    <td className="text-center">
                                        {normalizeDecimal(view.taxValueNum)}
                                    </td>
                                    <td className="text-center">
                                        {(() => {
                                            const v = parseFloat(view.vatValueTax);
                                            const t = parseFloat(view.taxValueNum);
                                            if (isNaN(v) || isNaN(t)) return 'N/A';
                                            const result = (v * t) / 100 + t;
                                            return formatNumber(result.toFixed(2));
                                        })()}
                                    </td>
                                    <td
                                        className="text-center"
                                        style={{ color: '#2e5bee', fontWeight: '500' }}
                                    >
                                        {(() => {
                                            const v = parseFloat(view.vatValueTax);
                                            const t = parseFloat(view.taxValueNum);
                                            if (isNaN(v) || isNaN(t)) return formatNumber(0);
                                            const result = (v * t) / 100 + t;
                                            const converted =
                                                !view.isSysCurr && view.exchangeRate
                                                    ? result * view.exchangeRate
                                                    : result;
                                            return `${formatNumber(converted)} ${systemCurrencyLabel}`;
                                        })()}
                                    </td>
                                    <td className="text-center"></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* ADDITIONAL DETAILS + COST SUMMARY */}
            <div className="grid-2">
                <div className="card">
                    <h4>ADDITIONAL DETAILS</h4>
                    <h5>Note to supplier</h5>
                    <hr />
                    <span>{rev.note_to_supplier || 'No notes'}</span>
                </div>

                <div className="card">
                    <h4>COST SUMMARY</h4>
                    <table className="cost-summary-table">
                        <thead>
                            <tr className="table-header">
                                <th className="cell-left"></th>
                                <th className="cell-right header-currency">
                                    Total ({view.currencyName})
                                </th>
                                <th className="cell-right header-system">
                                    System Total ({systemCurrencyLabel})
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="table-row">
                                <td className="cell-left label">Total without VAT</td>
                                <td className="cell-right">
                                    {formatNumber(view.totalWoVat)} {view.currencyName}
                                </td>
                                <td className="cell-right system-value">
                                    {formatNumber(view.totalWoVatSystem)} {systemCurrencyLabel}
                                </td>
                            </tr>

                            <tr className="table-row">
                                <td className="cell-left label">Taxes without VAT</td>
                                <td className="cell-right">
                                    {formatNumber(view.taxValueNum)} {view.currencyName}
                                </td>
                                <td className="cell-right system-value">
                                    {formatNumber(view.taxValueSystem)} {systemCurrencyLabel}
                                </td>
                            </tr>

                            <tr className="table-row">
                                <td className="cell-left label">VAT</td>
                                <td className="cell-right">
                                    {formatNumber(view.vatValue)} {view.currencyName}
                                </td>
                                <td className="cell-right system-value">
                                    {formatNumber(view.vatValueSystem)} {systemCurrencyLabel}
                                </td>
                            </tr>

                            <tr className="total-row">
                                <td className="cell-left total-label">TOTAL</td>
                                <td className="cell-right total-value">
                                    <strong>
                                        {formatNumber(view.totalValue)} {view.currencyName}
                                    </strong>
                                </td>
                                <td className="cell-right total-system-value">
                                    <strong>
                                        {formatNumber(view.totalSystemCurrency)}{' '}
                                        {systemCurrencyLabel}
                                    </strong>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default HistoryPORevisions;